import { HOLDER_NAME_MAX_LENGTH, isValidIban, normalizeIban } from './iban.js';
import { assertCents, formatEuroPlain, sumCents } from './money.js';

/**
 * SEPA-Überweisungsdatei `pain.001.001.09` (Plan Welle 3, W3-Auszahlung, Entscheidung 6): eine Sammeldatei für das
 * Online-Banking des Vereins. BARSO überweist nichts und wickelt keine Zahlung ab, es erzeugt nur diese Datei; der Verein
 * lädt sie bei seiner Bank hoch und gibt sie dort frei.
 *
 * Nur IBANs aus dem EWR (keine BIC): Der Empfänger-Bank-Block `CdtrAgt` entfällt, die Bank des Auftraggebers steht als
 * `NOTPROVIDED`. Texte werden in den SEPA-Zeichensatz umgeschrieben (ä wird ae, ß wird ss, der Rest wird entfernt).
 * Beträge entstehen rein ganzzahlig aus Cent. Reine Funktionen, kein Zufall und keine Uhr: Kennung und Zeitpunkt kommen von außen,
 * gleiche Eingabe ergibt gleiche Datei.
 */

export const SEPA_NAME_MAX = HOLDER_NAME_MAX_LENGTH;
export const SEPA_REMITTANCE_MAX = 140;
export const SEPA_ID_MAX = 35;

const UMLAUTS: Readonly<Record<string, string>> = {
  ä: 'ae',
  ö: 'oe',
  ü: 'ue',
  Ä: 'Ae',
  Ö: 'Oe',
  Ü: 'Ue',
  ß: 'ss',
  ẞ: 'SS',
  '&': '+',
  æ: 'ae',
  Æ: 'AE',
  ø: 'o',
  Ø: 'O',
  å: 'a',
  Å: 'A',
  œ: 'oe',
  Œ: 'OE',
  ł: 'l',
  Ł: 'L',
  đ: 'd',
  Đ: 'D',
  þ: 'th',
  ð: 'd',
};

/** Erlaubter Zeichensatz der SEPA-Texte (EPC): Buchstaben, Ziffern und `/ - ? : ( ) . , ' +` samt Leerzeichen. */
const SEPA_ALLOWED = /^[A-Za-z0-9/\-?:().,'+ ]*$/;

/**
 * Schreibt Text in den SEPA-Zeichensatz um und kürzt auf `max` Zeichen: Umlaute und ß als Buchstabenpaare, übrige Akzente
 * weg (é wird e), „&“ wird „+“, alles andere Unbekannte fällt weg. Leerräume werden gefaltet. Nie Zeilenumbrüche.
 */
export function sepaText(text: string, max: number): string {
  let out = '';
  for (const char of text) {
    const mapped = UMLAUTS[char];
    if (mapped !== undefined) {
      out += mapped;
      continue;
    }
    const base = char.normalize('NFKD').replace(/[̀-ͯ]/g, '');
    out += Array.from(base)
      .map((part) => (SEPA_ALLOWED.test(part) && part !== '' ? part : /\s/.test(part) ? ' ' : ''))
      .join('');
  }
  return out.replace(/ +/g, ' ').trim().slice(0, max).trim();
}

/** Kennung (höchstens 35 Zeichen aus `A-Z a-z 0-9 -`): alles andere fällt weg. Wirft, wenn nichts übrig bleibt. */
export function sepaId(raw: string): string {
  const id = raw.replace(/[^A-Za-z0-9-]/g, '').slice(0, SEPA_ID_MAX);
  if (id === '') throw new TypeError('Die Kennung ist leer.');
  return id;
}

/** Maskiert die fünf XML-Zeichen `& < > " '`. */
export function xmlEscape(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

/** Ostersonntag nach der Gaußschen Osterformel (gregorianisch), als { month: 1 bis 12, day }. */
export function easterSunday(year: number): { month: number; day: number } {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return { month, day };
}

const DAY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

function parseDay(day: string): Date {
  const match = DAY_PATTERN.exec(day);
  if (match === null) throw new TypeError(`Ungültiges Datum: ${day}`);
  const [, y = '', m = '', d = ''] = match;
  const date = new Date(Date.UTC(Number(y), Number(m) - 1, Number(d)));
  if (formatDay(date) !== day) throw new TypeError(`Ungültiges Datum: ${day}`);
  return date;
}

function formatDay(date: Date): string {
  return `${String(date.getUTCFullYear()).padStart(4, '0')}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
}

/** Ist das ein TARGET2-Geschäftstag (vereinfacht): Montag bis Freitag außer 1.1., Karfreitag, Ostermontag, 1.5., 25.12. und 26.12.? */
export function isTargetBusinessDay(day: string): boolean {
  const date = parseDay(day);
  const weekday = date.getUTCDay();
  if (weekday === 0 || weekday === 6) return false;
  const month = date.getUTCMonth() + 1;
  const dayOfMonth = date.getUTCDate();
  if (
    (month === 1 && dayOfMonth === 1) ||
    (month === 5 && dayOfMonth === 1) ||
    (month === 12 && (dayOfMonth === 25 || dayOfMonth === 26))
  ) {
    return false;
  }
  const easter = easterSunday(date.getUTCFullYear());
  const easterDate = Date.UTC(date.getUTCFullYear(), easter.month - 1, easter.day);
  const offset = Math.round((date.getTime() - easterDate) / 86_400_000);
  return offset !== -2 && offset !== 1;
}

/** Der erste Bankarbeitstag NACH dem Kalendertag `day` („JJJJ-MM-TT“). */
export function nextTargetBusinessDay(day: string): string {
  const date = parseDay(day);
  for (let step = 0; step < 14; step += 1) {
    date.setUTCDate(date.getUTCDate() + 1);
    const candidate = formatDay(date);
    if (isTargetBusinessDay(candidate)) return candidate;
  }
  throw new RangeError('Kein Bankarbeitstag gefunden.');
}

export interface SepaTransfer {
  /** Eindeutige Kennung der Überweisung in der Datei (wird auf `A-Z a-z 0-9 -` und 35 Zeichen gebracht). */
  endToEndId: string;
  /** Empfänger (Kontoinhaber). */
  name: string;
  iban: string;
  /** Ganze Cent, mindestens 1. */
  cents: number;
  /** Verwendungszweck. */
  remittance: string;
}

export interface SepaFileInput {
  /** Kennung der Nachricht (höchstens 35 Zeichen), je Datei neu. */
  messageId: string;
  /** Erstellungszeitpunkt als Berliner Wanduhr „JJJJ-MM-TTThh:mm:ss“. */
  createdAt: string;
  /** Ausführungsdatum „JJJJ-MM-TT“. */
  executionDate: string;
  /** Auftraggeber (Konto des Vereins). */
  debtor: { name: string; iban: string };
  transfers: readonly SepaTransfer[];
}

const CREATED_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/;

const element = (indent: number, tag: string, value: string): string =>
  `${'  '.repeat(indent)}<${tag}>${xmlEscape(value)}</${tag}>`;

/** Summe der Beträge als Text, z. B. „123.45“ (aus denselben Cent-Werten wie die Einzelbeträge). */
export function sepaControlSum(transfers: readonly { cents: number }[]): string {
  return formatEuroPlain(sumCents(transfers.map((transfer) => transfer.cents)));
}

/**
 * Baut die Datei. Wirft bei ungültigen Angaben (keine Überweisung, ungültige IBAN, Betrag unter 1 Cent, doppelte Kennung,
 * Name nach der Umschreibung leer, falsches Datum). Kopf und Zahlungsblock tragen dieselbe Summe und Anzahl.
 */
export function buildSepaXml(input: SepaFileInput): string {
  if (input.transfers.length === 0) throw new RangeError('Keine Überweisung in der Datei.');
  if (!CREATED_PATTERN.test(input.createdAt)) throw new TypeError('Ungültiger Zeitpunkt.');
  parseDay(input.executionDate);
  const debtorIban = normalizeIban(input.debtor.iban);
  if (!isValidIban(debtorIban)) throw new TypeError('Die IBAN des Auftraggebers ist ungültig.');
  const debtorName = sepaText(input.debtor.name, SEPA_NAME_MAX);
  if (debtorName === '') throw new TypeError('Der Name des Auftraggebers fehlt.');
  const messageId = sepaId(input.messageId);

  const seen = new Set<string>();
  const items = input.transfers.map((transfer) => {
    assertCents(transfer.cents);
    if (transfer.cents < 1) throw new RangeError('Jeder Betrag muss mindestens 1 Cent sein.');
    const iban = normalizeIban(transfer.iban);
    if (!isValidIban(iban)) throw new TypeError('Eine IBAN ist ungültig.');
    const name = sepaText(transfer.name, SEPA_NAME_MAX);
    if (name === '') throw new TypeError('Ein Name des Empfängers fehlt.');
    const endToEndId = sepaId(transfer.endToEndId);
    if (seen.has(endToEndId)) throw new TypeError('Doppelte Kennung einer Überweisung.');
    seen.add(endToEndId);
    return {
      endToEndId,
      name,
      iban,
      cents: transfer.cents,
      remittance: sepaText(transfer.remittance, SEPA_REMITTANCE_MAX),
    };
  });
  const count = String(items.length);
  const controlSum = sepaControlSum(items);

  const lines: string[] = [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<Document xmlns="urn:iso:std:iso:20022:tech:xsd:pain.001.001.09" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">',
    '  <CstmrCdtTrfInitn>',
    '    <GrpHdr>',
    element(3, 'MsgId', messageId),
    element(3, 'CreDtTm', input.createdAt),
    element(3, 'NbOfTxs', count),
    element(3, 'CtrlSum', controlSum),
    '      <InitgPty>',
    element(4, 'Nm', debtorName),
    '      </InitgPty>',
    '    </GrpHdr>',
    '    <PmtInf>',
    element(3, 'PmtInfId', sepaId(`${messageId}-1`)),
    element(3, 'PmtMtd', 'TRF'),
    element(3, 'NbOfTxs', count),
    element(3, 'CtrlSum', controlSum),
    '      <PmtTpInf>',
    '        <SvcLvl>',
    element(5, 'Cd', 'SEPA'),
    '        </SvcLvl>',
    '      </PmtTpInf>',
    '      <ReqdExctnDt>',
    element(4, 'Dt', input.executionDate),
    '      </ReqdExctnDt>',
    '      <Dbtr>',
    element(4, 'Nm', debtorName),
    '      </Dbtr>',
    '      <DbtrAcct>',
    '        <Id>',
    element(5, 'IBAN', debtorIban),
    '        </Id>',
    '      </DbtrAcct>',
    '      <DbtrAgt>',
    '        <FinInstnId>',
    '          <Othr>',
    element(6, 'Id', 'NOTPROVIDED'),
    '          </Othr>',
    '        </FinInstnId>',
    '      </DbtrAgt>',
    element(3, 'ChrgBr', 'SLEV'),
  ];
  for (const item of items) {
    lines.push(
      '      <CdtTrfTxInf>',
      '        <PmtId>',
      element(5, 'EndToEndId', item.endToEndId),
      '        </PmtId>',
      '        <Amt>',
      `          <InstdAmt Ccy="EUR">${formatEuroPlain(item.cents)}</InstdAmt>`,
      '        </Amt>',
      '        <Cdtr>',
      element(5, 'Nm', item.name),
      '        </Cdtr>',
      '        <CdtrAcct>',
      '          <Id>',
      element(6, 'IBAN', item.iban),
      '          </Id>',
      '        </CdtrAcct>',
      '        <RmtInf>',
      element(5, 'Ustrd', item.remittance === '' ? '-' : item.remittance),
      '        </RmtInf>',
      '      </CdtTrfTxInf>',
    );
  }
  lines.push('    </PmtInf>', '  </CstmrCdtTrfInitn>', '</Document>', '');
  return lines.join('\n');
}
